import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { backupStatus, forgetDevice } from '@/services/appSession';
import { useSession } from '../state/session';
import { flushAll } from '../state/saves';
import { Icon, type IconName } from '../components/Icon';
import { UpdatePrompt } from './UpdatePrompt';
import { SyncBadge } from './SyncBadge';
import { ProgressDock, useCurrentClientId } from './ProgressDock';
import { refreshApp, usePullToRefresh } from './refresh';
import styles from './Layout.module.css';

/** PC 너비인지 (계정·새로고침·로그아웃을 왼쪽 메뉴에 둘지, 위 줄에 둘지) */
function useWide(): boolean {
  const q = '(min-width: 1200px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

type NavItem = { to: string; label: string; icon: IconName; run?: boolean; end?: boolean };

/**
 * 폰: 아래 탭바 (가운데 큰 회기 진행 버튼) / 태블릿: 왼쪽 아이콘 줄 / PC: 왼쪽 메뉴.
 * 회기 진행: 내담자 화면에 있으면 그 내담자로 바로, 아니면 내담자 고르기.
 */
export function Layout() {
  const lock = useSession((s) => s.lock);
  const accountName = useSession((s) => s.accountName);
  const location = useLocation();
  const clientId = useCurrentClientId();
  const [backupOverdue, setBackupOverdue] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const ptr = usePullToRefresh();
  const wide = useWide();

  useEffect(() => {
    void backupStatus().then((b) => setBackupOverdue(b.overdue));
  }, [location.pathname]);

  const runActive = /\/run(\/|$|\?)/.test(location.pathname) || location.pathname === '/run';
  const items: NavItem[] = [
    { to: '/', label: '홈', icon: 'home', end: true },
    { to: '/clients', label: '내담자', icon: 'users' },
    { to: clientId ? `/clients/${clientId}/run` : '/run', label: '회기 진행', icon: 'play', run: true },
    { to: '/search', label: '검색', icon: 'search' },
    { to: '/settings', label: '설정', icon: 'settings' },
  ];
  const refresh = () => {
    setRefreshing(true);
    void refreshApp();
  };
  const logout = () => void flushAll().finally(() => void forgetDevice().finally(lock));

  return (
    <div className={styles.shell}>
      <nav className={styles.nav} aria-label="주 메뉴">
        <div className={styles.brand}>SDCare</div>
        {items.map((it) => (
          <NavLink
            key={it.label}
            to={it.to}
            end={!!it.end}
            className={({ isActive }) =>
              [
                styles.link,
                it.run ? styles.run : '',
                (it.run ? runActive : isActive && !runActive) ? styles.active : '',
              ].join(' ')
            }
          >
            <span className={styles.icon}>
              <Icon name={it.icon} filled={!!it.run} size={it.run ? 24 : 22} />
            </span>
            <span className={styles.label}>{it.label}</span>
          </NavLink>
        ))}
        {wide && (
          <div className={styles.side}>
            <SyncBadge />
            {accountName && <span className="muted small">{accountName} 님</span>}
            <button type="button" className={styles.sideBtn} disabled={refreshing} onClick={refresh}>
              <Icon name="refresh" size={18} /> {refreshing ? '새로고침 중…' : '새로고침'}
            </button>
            <button type="button" className={styles.sideBtn} onClick={logout}>
              <Icon name="logout" size={18} /> 로그아웃
            </button>
          </div>
        )}
      </nav>
      <main className={styles.main}>
        {!wide && <ProgressDock />}
        {ptr.pull > 0 && (
          <div className={styles.ptr} style={{ height: ptr.pull }} aria-live="polite">
            {ptr.refreshing ? '새로고침 중…' : ptr.pull >= 80 ? '놓으면 새로고침' : '↓ 당겨서 새로고침'}
          </div>
        )}
        {!wide && (
          <div className={styles.topbar}>
            <SyncBadge />
            {accountName && <span className="muted small">{accountName} 님</span>}
            <span className={styles.grow} />
            <button
              type="button"
              className={styles.iconBtn}
              title="새로고침 (입력 중인 내용은 먼저 저장)"
              disabled={refreshing}
              onClick={refresh}
            >
              <Icon name="refresh" size={18} />
              <span className={styles.btnText}>{refreshing ? '새로고침 중…' : '새로고침'}</span>
            </button>
            <button type="button" className={styles.iconBtn} onClick={logout}>
              <Icon name="logout" size={18} />
              <span className={styles.btnText}>로그아웃</span>
            </button>
          </div>
        )}
        <div className={styles.body}>
          <div className={styles.content}>
            <UpdatePrompt />
            {backupOverdue && location.pathname !== '/settings' && (
              <div role="status" className="banner">
                백업한 지 7일이 넘었습니다. <Link to="/settings">백업하기</Link>
              </div>
            )}
            <Outlet />
          </div>
          {wide && <ProgressDock rail />}
        </div>
      </main>
    </div>
  );
}
